import React, {Component, useCallback, useEffect, useState} from 'react';
import {Location} from '@reach/router';
import {Button, Card, Col, Container, Row} from 'reactstrap';
import PageMeta from 'components/PageMeta';
import ABTest, {DebugMenu} from 'react-controlled-ab';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import Header from 'components/Header';
import OptinModal, {OptinModalTrigger} from 'components/OptinModal';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCalendarCheck, faClock, faShareSquare} from '@fortawesome/pro-light-svg-icons';
import queryString from 'query-string';
import OptInForm from 'components/OptInForm';
import CtaOptin from 'components/OptinCta';
import Testimonial from 'components/Testimonial';
import Footer from 'components/Footer';
import ShareButtons from 'components/ShareButtons';
import johnHeadshotV2 from 'static/speaker/john-robbins-headshot-20200622.jpg';
import MasterclassReregisterForm from 'components/MasterclassReregisterForm';
import {useUserState} from 'contexts/user';
import {usePhaseState} from 'funnel-schedule/phase-context';
import TimerSection from 'components/Landing/TimerSection';
import classNames from 'classnames';
import Section, {SectionImage, SectionAccordion} from 'components/Elements/Section';
import IconList, {IconListItem} from 'components/Elements/IconList';
import MasterclassInfographicForm from 'components/MasterclassInfographicForm';
import HeaderVideo from 'components/Elements/HeaderVideo';
import useBalanceText from 'utils/useBalanceText';

export default function (props) {
  return (
    <Location>
      {({location, navigate}) => <MasterClassIndex location={location} navigate={navigate} {...props} />}
    </Location>
  );
}
function MasterClassIndex({
  location = {},
  noShare = false,
  noFixedVideo = true,
  linkLogo = false,
  slug = 'masterclass',
  navigate,
  variant = null,
  fromEntry = 'Masterclass',
  pageTitle = 'Join the Food For Health Masterclass with John Robbins',
  pageDescription = 'Unlock your unique body’s ultimate potential through this Masterclass with John Robbins where he talks about how you can make food the foundation of YOUR health with these 10 mighty plant-powered breakthroughs. This Masterclass is only available for a limited time, so register today!',
  shareUrl = 'https://thriving.foodrevolution.org/masterclass/',
  shareImg = 'https://cdn.foodrevolution.org/ppt/social/ppt-ogimage-v20191018.jpg',
  facebookDescription = 'John Robbins is one of the world’s most beloved natural health experts and inspired millions of people to claim the power of their food choices to take a stand for a healthy life. Right now, you have a very special limited-time opportunity to join John for a totally free Masterclass on how you can make food the foundation of YOUR health. Click here to join him...',
  twitterDescription = 'Find out about 10 mighty #plantpowered breakthroughs that could save millions of lives in this free #masterclass with world-renowned author, John Robbins. Join the FREE #foodforhealthmasterclass here:',
  twitterHashtags = [null],
  twitterImg = 'https://cdn.foodrevolution.org/ppt/social/ppt-ogimage-v20191018.jpg',
  pinterestDescription = 'Looking for a sign that it’s time to take charge of your healthy-eating habits? This is it. Join this free masterclass and get the inspiration you need to make your healthy habits stick. Watch here now: https://thriving.foodrevolution.org/masterclass/',
  pinterestImg = 'https://cdn.foodrevolution.org/ppt/social/ppt-ogimage-v20191018.jpg',
  ...props
}) {
  const {email} = useUserState();
  const qs = typeof window !== 'undefined' ? queryString.parse(window.location.search) : {};
  const {fromInfographic} = qs.fromInfographic ? qs : location.state || {};
  const navigateWatch = useCallback(() => navigate('/masterclass/watch/'), []);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const pathName = window.location.pathname;
      const pathRegex = new RegExp(`\/${slug}\/`, 'g');
      if (!pathName.match(pathRegex)) {
        navigate(`/${slug}/` + location.search);
      }
    }
  }, []);

  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: '',
        launchTimerHeading: '',
        launchEndTitle: ''
      }
    }
  } = usePhaseState();

  const launchEnd = new Date(defaultSchedule.launchEnd).getTime();

  const ctaButton =
    fromInfographic && email ? (
      <MasterclassInfographicForm fromInfographic={fromInfographic} />
    ) : (
      <OptinModalTrigger>
        {fromEntry === 'plants' ? (
          <>Yes! Sign Me Up!</>
        ) : (
          <>
            Reserve Your <br className="d-sm-none d-lg-block d-xl-none" />
            Free Spot Now
          </>
        )}
      </OptinModalTrigger>
    );

  const mobileImage = (
    <div className="d-lg-none">
      <Row className="d-flex justify-content-center my-4">
        <Col xs="8" sm="7">
          <img className="img-fluid border-solid border-white border-5 rounded-circle" src={johnHeadshotV2} />
        </Col>
      </Row>
    </div>
  );

  const headerLaunchContent = (
    <>
      {defaultSchedule.phase === 'during' ? (
        <TimerSection
          launchEnd={launchEnd}
          timerHeading={defaultSchedule.launchTimerHeading}
          className="text-center mb-4"
          timerHeadingClass={defaultSchedule.timerHeadingClass}
        />
      ) : null}

      {defaultSchedule.phase === 'after' ? <h4 className="my-4">{defaultSchedule.launchEndTitle}</h4> : null}
    </>
  );

  const controlSpeakerSections = (
    <>
      <SectionImage
        id="section-speaker-john"
        color="black"
        innerClass="text-white"
        imgPosition="right"
        imgBgPosition="center-top"
        imgUrl={require('static/speaker/john-robbins-veggies-square.jpg')}
      >
        <h4 className="section-heading">Who is John Robbins?</h4>
        <p>
          John Robbins was groomed to be the heir to the Baskin-Robbins ice cream empire, which was founded and owned by
          his father. He had money, prestige, and security, along with an ice cream cone-shaped swimming pool in the
          backyard.
        </p>
        <p>
          So why did John walk away from a path that was practically “paved with gold and ice cream”? Because his
          conscience was emerging. He didn’t want to devote his life to selling ice cream after realizing it makes
          people unhealthy. So, he decided to make a change and forge a new path.
        </p>
        <p>
          Over the last 35 years, John’s books about healthy eating and healthy living (including the blockbuster
          bestseller, <i>Diet for a New America</i>) have sold millions of copies and been translated into more than 30
          languages. He has made hundreds of local and national radio and television appearances, spoken in person to
          hundreds of thousands of people, founded the nonprofit, EarthSave International, and received dozens of awards
          for his work on behalf of healthy people and a healthy planet.
        </p>
        <p>With his son and colleague, Ocean Robbins, John serves as co-host of Food Revolution Network.</p>
      </SectionImage>

      <SectionImage
        id="section-speaker-ocean"
        color="black"
        innerClass="text-white"
        imgPosition="left"
        imgBgPosition="center-top"
        imgUrl={require('static/speaker/ocean-robbins-veggies-square.jpg')}
        imgUrlMobile={require('static/speaker/ocean-robbins-veggies-crop.jpg')}
      >
        <h4 className="section-heading">Hosted by Ocean Robbins</h4>
        <p>
          Ocean Robbins was born in a log cabin built by his parents and was raised on food they grew on the land
          together. He has reached millions of people from 180+ nations in schools, conferences, and online events.
        </p>
        <p>
          Ocean’s work has taken him all over the world, where he has seen first-hand the powerful impact of the food we
          eat — not just on our health, but on people and economies everywhere. He has served as an adjunct professor
          for Chapman University and is author of the national bestseller, <i>31-Day Food Revolution.</i>
        </p>
        <p>He serves as CEO of Food Revolution Network.</p>
      </SectionImage>
    </>
  );

  useBalanceText();
  return (
    <div className={classNames(`page d-flex flex-column ${noShare ? 'page-nosocial' : null}`)}>
      <PageMeta title={pageTitle} description={pageDescription} url={shareUrl} ogImage={shareImg} />

      <div className="page-content">
        {fromInfographic ? (
          <section className="section-infographic-info background-green text-white py-4">
            <Container>
              <Row className="d-flex align-items-center">
                <Col xs="3" md="2" className="text-center">
                  <img
                    className="img-infographic img-fluid"
                    src="https://cdn.foodrevolution.org/ppt/frn-infographic-foodslongevity.png"
                  />
                </Col>
                <Col xs="9" md="10">
                  Thanks for taking an interest in food and your health! Your infographic of{' '}
                  <em>10 Foods to Eat and 10 Foods to Avoid for Health and Longevity</em> should be in your email inbox
                  in a few minutes.
                </Col>
              </Row>
            </Container>
          </section>
        ) : null}

        <Header
          className="background-purple header-wrap"
          logoWhite
          style="dark"
          linkLogo={linkLogo}
          shareUrl={shareUrl}
          shareImg={shareImg}
          facebookDescription={facebookDescription}
          twitterDescription={twitterDescription}
          pinterestDescription={pinterestDescription}
          pinterestImg={pinterestImg}
        />

        {fromEntry === 'plants' ? (
          <Section
            id="section-masterclass-header-cta"
            className="background-image-1203599923 background-image-center background-cover background-overlay-black-90 section-angle-first text-center text-white"
            color="black"
            angle="none"
          >
            <Container>
              <Row className="align-items-center justify-content-center">
                <Col xs="12" lg="7">
                  <p>
                    Plant-based eating is the #1 diet for <br className="d-none d-lg-block" />
                    your health and for the planet. But some <br className="d-none d-xl-block" />
                    people are doing it wrong and getting sick.
                  </p>
                  <p>
                    Find out how to do it right — so <br className="d-none d-lg-block d-xl-none" />
                    you can reap the benefits!
                  </p>{' '}
                  <h3 className="mt-0">
                    Get the free <i>Guide To Going Plant-Based</i> AND Get A Free Spot in the Food For Health
                    Masterclass
                  </h3>
                  <p>
                    In this FREE Masterclass, John Robbins <br className="d-none d-lg-block d-xl-none" />
                    reveals <br className="d-none d-sm-block d-lg-none d-xl-block" />
                    10 mighty, plant-powered breakthroughs that can{' '}
                    <br className="d-none d-sm-block d-lg-none d-xl-block" />
                    unlock your <br className="d-none d-lg-block d-xl-none" />
                    unique body’s ultimate health potential.
                  </p>
                </Col>
                <Col xs="12" lg="5">
                  <div className="img-guidebook mb-4">
                    <img
                      src="https://cdn.foodrevolution.org/global/guidebook/guide-to-going-plant-based.png"
                      style={{maxHeight: '300px'}}
                    />
                  </div>
                  {ctaButton}
                </Col>
              </Row>
            </Container>
          </Section>
        ) : (
          <Section
            id="section-masterclass-header-cta"
            className={classNames(
              `section-angle-first section-header-optin section-header-optin-landing section-header-optin-landing-${
                defaultSchedule.phase
              } section-header-optin-landing-right text-white ${
                variant === 'ad' ? 'section-header-optin-landing-ad-v1' : ''
              }`
            )}
            color="black"
            innerClass="text-white text-center"
            angle="none"
          >
            <Container>
              <Row className="d-flex justify-content-center justify-content-lg-start align-items-center">
                <Col xs="12" lg="7" xl="6">
                  {fromInfographic ? (
                    <>
                      <div className="infographic-headlines">
                        <h3 className="section-heading">
                          Now take the next step. Learn the secrets to plant-powered thriving.
                        </h3>
                        <h6>
                          Join John Robbins, one of the world's most beloved natural health experts, as he shows you 10
                          mighty, science-backed breakthroughs that can unlock your ultimate health potential.
                        </h6>
                      </div>
                      {mobileImage}
                      <h6 className="text-400 mt-0 mb-4">
                        <span className="ppt-class-headline">
                          <em>Food For Health Masterclass</em>
                        </span>
                      </h6>
                      {headerLaunchContent}
                      {ctaButton}
                    </>
                  ) : (
                    <>
                      {variant === 'ad' ? (
                        <>
                          <h6 className="section-heading text-400 my-3">Now Playing FREE For A Limited Time!</h6>
                          <h3 className="my-4">
                            Discover John Robbins’ 10 Mighty, Plant-Powered Breakthroughs That Can Unlock The Body’s
                            Ultimate Health Potential
                          </h3>
                          {ctaButton}
                          <h6 className="mt-4 mb-4">
                            Join one of the world’s most beloved experts on living a plant-powered lifestyle in this
                            Masterclass.
                          </h6>
                          {mobileImage}
                          <h6 className="text-400 mt-0 mb-3">
                            <span className="infographic-class-headline">
                              Transform the way you think, eat, and live with the
                            </span>
                          </h6>
                          <img
                            src="https://cdn.foodrevolution.org/ppt/ppt-masterclass-logo-white.svg"
                            style={{maxHeight: '90px'}}
                          />
                        </>
                      ) : (
                        <>
                          <div className="ppt-headlines">
                            <h6 className="section-heading text-400 my-3">Now Playing FREE For A Limited Time!</h6>
                            <h3 className="mt-4">Transform the way you think, eat, and live with the</h3>
                            <img
                              src="https://cdn.foodrevolution.org/ppt/ppt-masterclass-logo-white.svg"
                              style={{maxHeight: '90px'}}
                            />
                            <h6 className="mb-4">
                              In this FREE Masterclass, John Robbins reveals 10 mighty, plant-powered breakthroughs that
                              can unlock your unique body’s ultimate health potential.
                            </h6>
                          </div>
                          {mobileImage}
                          {headerLaunchContent}
                          {ctaButton}
                        </>
                      )}
                    </>
                  )}
                </Col>
              </Row>
            </Container>
          </Section>
        )}

        <Section id="section-video" color="light-gray" innerClass="text-center" angle="both">
          <Container>
            <Row className="d-flex align-items-center">
              <Col xs="12" lg="5" className="order-lg-2 mb-4 mb-lg-0">
                {fromEntry === 'plants' ? (
                  <>
                    <img
                      className="img-fluid border-solid border-white border-5 rounded-circle mb-3"
                      src={johnHeadshotV2}
                      style={{maxHeight: '200px'}}
                    />
                  </>
                ) : null}
                <h4 className="section-only-heading">
                  Prepare to make food the foundation of your HEALTH with this FREE Masterclass
                </h4>
              </Col>
              <Col xs="12" lg="7" className="order-lg-1 px-0 px-sm-3">
                <div className="mb-3">
                  <HeaderVideo
                    url="https://vimeo.com/367342255/675c7c6578"
                    headline={
                      <>
                        <h4 className="section-heading text-white">
                          Join in the <i>Food For Health Masterclass</i>
                        </h4>
                      </>
                    }
                    optin
                    noFixed={noFixedVideo}
                  />
                </div>
                <div className="px-3 px-sm-0">
                  <p className="mb-0">
                    Watch this 2-min trailer <br className="d-none d-lg-block d-xl-none" />
                    for John Robbins’ Masterclass.
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </Section>

        {variant === 'ad' ? null : (
          <Section id="section-steps" color="white">
            <Container>
              <Row className="d-flex align-items-stretch justify-content-center text-center">
                <Col xs="12" lg="4" className="d-flex flex-column mb-5 mb-lg-0">
                  <div className="icon">
                    <FontAwesomeIcon className="text-green text-large" icon={faCalendarCheck} />
                  </div>
                  <h5>Add to Your Calendar</h5>
                  <p className="my-auto">Clear up your schedule, and treat yourself to some me-time.</p>
                </Col>
                <Col xs="12" lg="4" className="d-flex flex-column mb-5 mb-lg-0">
                  <div className="icon">
                    <FontAwesomeIcon className="text-green text-large" icon={faClock} />
                  </div>
                  <h5>80 Minutes</h5>
                  <p className="my-auto">
                    Schedule approximately <br className="d-none d-xl-block" />
                    80 minutes <br className="d-none d-sm-block d-lg-none" />
                    of quiet time <br className="d-none d-xl-block" />
                    for this Masterclass.
                  </p>
                </Col>
                <Col xs="12" lg="4" className="d-flex flex-column">
                  <div className="icon">
                    <FontAwesomeIcon className="text-green text-large" icon={faShareSquare} />
                  </div>
                  <h5>Share This Masterclass</h5>
                  <p className="my-auto">
                    Everyone can benefit <br className="d-none d-xl-block" />
                    from {variant === 'ad' ? 'plant-based health facts' : 'learning how to heal'}.{' '}
                    <br className="d-none d-sm-block d-lg-none" />
                    Share this Masterclass with your friends and family.
                  </p>
                </Col>
              </Row>
            </Container>
          </Section>
        )}

        <Section id="section-list" color="green">
          <Container className="py-5">
            <Row className="m-0">
              <Col xs="12" className="p-0">
                <Container className="background-white box-shadow-black-25 rounded p-4 py-lg-5" fluid>
                  <Row className="my-3">
                    <Col xs="12">
                      {variant === 'ad' ? (
                        <>
                          <h4 className="section-heading text-center">In This 80-Minute Masterclass, Get Ready For:</h4>
                          <Row className="d-flex justify-content-center">
                            <Col xs="12" lg="11">
                              <IconList className="mb-0">
                                <IconListItem>
                                  <h5 className="section-heading text-green mb-1">The big food lies</h5>
                                  <p>
                                    The food industry relies on some pretty dark myths they’d rather we didn’t know.
                                    John will shed light on the{' '}
                                    <b>most dangerous food lies to stop buying into right now.</b>
                                  </p>
                                </IconListItem>
                                <IconListItem>
                                  <h5 className="section-heading text-green mb-1">
                                    10 mighty plant-powered breakthroughs
                                  </h5>
                                  <p>
                                    John shares 10 powerful breakthroughs <b>that are super easy</b> to implement, and
                                    that can <b>help us live our healthiest life</b> while reducing stress and
                                    increasing happiness.
                                  </p>
                                </IconListItem>
                                <IconListItem>
                                  <h5 className="section-heading text-green mb-1">
                                    How what we eat, and what we don’t eat, can help save the planet
                                  </h5>
                                  <p>
                                    Ever feel concerned about the state of the world? Want to be part of the solution?
                                    Good news! Our food choices can help save topsoil so our grandkids can grow food,
                                    save animals from cruelty, and protect the climate of our planet.
                                  </p>
                                </IconListItem>
                                <IconListItem>
                                  <h5 className="section-heading text-green mb-1">John’s inspiring story</h5>
                                  <p>
                                    John shares how he walked away from inheriting the Baskin-Robbins ice cream empire
                                    and went on to be considered one of the most powerful spokespersons in the world for
                                    a sane, ethical, and sustainable future.
                                  </p>
                                </IconListItem>
                                <IconListItem>
                                  <h5 className="section-heading text-green mb-1">
                                    Why taking control of health matters
                                  </h5>
                                  <p>
                                    Get inspired like never before to <b>take health to the next level.</b>
                                  </p>
                                </IconListItem>
                                <IconListItem>
                                  <h5 className="section-heading text-green mb-1">Receive a special invitation</h5>
                                  <p>
                                    Gain access to John and Ocean’s landmark online course, Plant-Powered & Thriving,
                                    for a massive discount. Stay to the end to find out all about it.
                                  </p>
                                </IconListItem>
                                <IconListItem>
                                  <h5 className={classNames(`section-heading text-green mb-0`)}>And so much more…</h5>
                                </IconListItem>
                              </IconList>
                            </Col>
                          </Row>
                        </>
                      ) : (
                        <>
                          <h4 className="section-heading text-center">
                            In This 80-Minute Masterclass, You’ll Discover:
                          </h4>
                          <Row className="d-flex justify-content-center">
                            <Col xs="12" lg="11">
                              <IconList className="mb-0">
                                <IconListItem>
                                  <h5 className="section-heading text-green mb-1">Why your health matters</h5>
                                  <p>
                                    Get inspired like never before to <b>take your health into your hands</b> — where it
                                    belongs!
                                  </p>
                                </IconListItem>
                                <IconListItem>
                                  <h5 className="section-heading text-green mb-1">The big food lies</h5>
                                  <p>
                                    The food industry has some pretty dark secrets they’d rather you didn’t know. John
                                    will shed light on the <b>most dangerous food lies you need to know right now.</b>
                                  </p>
                                </IconListItem>
                                <IconListItem>
                                  <h5 className="section-heading text-green mb-1">
                                    10 mighty plant-powered breakthroughs
                                  </h5>
                                  <p>
                                    Discover 10 powerful breakthroughs <b>that are super easy</b> to implement, and that
                                    can <b>help you live your healthiest life</b> while reducing stress and increasing
                                    happiness and joy.
                                  </p>
                                </IconListItem>
                                <IconListItem>
                                  <h5 className="section-heading text-green mb-1">
                                    How what you eat, and what you don’t eat, can help save the planet
                                  </h5>
                                  <p>
                                    Ever feel concerned about the state of the world? Wish you could be part of the
                                    solution? You can! Your food choices can help save topsoil so our grandkids can grow
                                    food, save animals from cruelty, and protect the climate of our planet —
                                    deliciously!
                                  </p>
                                </IconListItem>
                                <IconListItem>
                                  <h5 className="section-heading text-green mb-1">John’s inspiring story</h5>
                                  <p>
                                    Learn how John walked away from inheriting the Baskin-Robbins ice cream empire and
                                    went on to be considered one of the most eloquent and powerful spokespersons in the
                                    world for a sane, ethical, and sustainable future.
                                  </p>
                                </IconListItem>
                                <IconListItem>
                                  <h5 className="section-heading text-green mb-1">Receive a special invitation</h5>
                                  <p>
                                    Gain access to John and Ocean’s landmark online course, Plant-Powered & Thriving,
                                    for a massive discount. Stay to the end to find out all about it.
                                  </p>
                                </IconListItem>
                                <IconListItem>
                                  <h5 className={classNames(`section-heading text-green mb-0`)}>And so much more…</h5>
                                </IconListItem>
                              </IconList>
                            </Col>
                          </Row>
                        </>
                      )}
                    </Col>
                  </Row>
                </Container>
              </Col>
            </Row>
          </Container>
        </Section>

        {variant === 'ad' ? (
          <>
            <SectionAccordion
              id="section-speaker-john"
              color="black"
              innerClass="text-white"
              imgPosition="right"
              imgBgPosition="center-top"
              imgUrl={require('static/speaker/john-robbins-veggies-square.jpg')}
              title="Who is John Robbins?"
              titleStyle="text-white text-h4 text-800"
            >
              <p>
                John Robbins was groomed to be the heir to the Baskin-Robbins ice cream empire, which was founded and
                owned by his father. He had money, prestige, and security, along with an ice cream cone-shaped swimming
                pool in the backyard.
              </p>
              <p>
                So why did John walk away from a path that was practically “paved with gold and ice cream”? Because his
                conscience was emerging. He didn’t want to devote his life to selling ice cream after realizing it makes
                people unhealthy. So, he decided to make a change and forge a new path.
              </p>
              <p>
                Over the last 35 years, John’s books about healthy eating and healthy living (including the blockbuster
                bestseller, <i>Diet for a New America</i>) have sold millions of copies and been translated into more
                than 30 languages. He has made hundreds of local and national radio and television appearances, spoken
                in person to hundreds of thousands of people, founded the nonprofit, EarthSave International, and
                received dozens of awards for his work on behalf of healthy people and a healthy planet.
              </p>
              <p>With his son and colleague, Ocean Robbins, John serves as co-host of Food Revolution Network.</p>
            </SectionAccordion>
            <SectionAccordion
              id="section-speaker-ocean"
              color="black"
              innerClass="text-white"
              imgPosition="left"
              imgBgPosition="center-top"
              imgUrl={require('static/speaker/ocean-robbins-veggies-square.jpg')}
              imgUrlMobile={require('static/speaker/ocean-robbins-veggies-crop.jpg')}
              title="Hosted by Ocean Robbins"
              titleStyle="text-white text-h4 text-800"
            >
              <p>
                Ocean Robbins was born in a log cabin built by his parents and was raised on food they grew on the land
                together. He has reached millions of people from 180+ nations in schools, conferences, and online
                events.
              </p>
              <p>
                Ocean’s work has taken him all over the world, where he has seen first-hand the powerful impact of the
                food we eat — not just on our health, but on people and economies everywhere. He has served as an
                adjunct professor for Chapman University and is author of the national bestseller,{' '}
                <i>31-Day Food Revolution.</i>
              </p>
              <p>He serves as CEO of Food Revolution Network.</p>
            </SectionAccordion>
          </>
        ) : (
          controlSpeakerSections
        )}

        <Section id="section-testimonials" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h4 className="section-heading text-center">
                  Here’s what some of the world’s leading figures have said about John Robbins.
                </h4>
                <Row className="d-flex align-items-stretch justify-content-center">
                  <Col xs="12" md="6" lg="4" className="mb-4">
                    <Testimonial id="woody-harrelson" />
                  </Col>
                  {variant === 'ad' ? null : (
                    <>
                      <Col xs="12" md="6" lg="4" className="mb-4">
                        <Testimonial id="paul-hawken" />
                      </Col>
                      <Col xs="12" md="6" lg="4" className="mb-4">
                        <Testimonial id="marianne-williamson" />
                      </Col>
                    </>
                  )}
                  <Col xs="12" md="6" lg="4" className="mb-4">
                    <Testimonial id="paul-mccartney" />
                  </Col>
                  {variant === 'ad' ? null : (
                    <>
                      <Col xs="12" md="6" lg="4" className="mb-4">
                        <Testimonial id="cory-booker" />
                      </Col>
                    </>
                  )}
                  <Col xs="12" md="6" lg="4" className="mb-4">
                    <Testimonial id="kris-carr" />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </Section>

        <SectionImage
          id="landing-cta"
          className={variant === 'ad' ? 'landing-final-cta landing-final-cta-ads' : 'landing-final-cta'}
          color="purple"
          innerClass="text-white text-center"
          imgPosition="left"
          imgUrl={require('static/backgrounds/father-daughter-market-square.jpg')}
          imgUrlMobile={require('static/backgrounds/father-daughter-market.jpg')}
          angle="none"
        >
          <h2 className="section-heading">
            Thousands of people will experience this life-changing event. Don’t miss it!
          </h2>
          <h4 className="text-400 m-0">
            <em>Take the Power to Create Health Into Your Hands — Where it Belongs!</em>
          </h4>

          {variant === 'ad' ? null : (
            <>
              {defaultSchedule.phase === 'during' ? (
                <TimerSection
                  launchEnd={launchEnd}
                  timerHeading={defaultSchedule.launchTimerHeading}
                  className="text-center mb-4"
                  timerHeadingClass={defaultSchedule.timerHeadingClass}
                />
              ) : null}

              {defaultSchedule.phase === 'after' ? <h4 className="my-4">{defaultSchedule.launchEndTitle}</h4> : null}
            </>
          )}

          <div className="mt-4">{ctaButton}</div>

          <p className="mt-4 mb-0">
            This Masterclass is absolutely FREE <br className="d-none d-sm-block d-md-none d-lg-block" />
            and plays in your local time.
          </p>
        </SectionImage>
      </div>
      <div className="footer-content my-auto">
        <Footer />
        {noShare ? null : (
          <ShareButtons
            pageDescription={pageDescription}
            shareUrl={shareUrl}
            shareImg={shareImg}
            facebookDescription={facebookDescription}
            twitterDescription={twitterDescription}
            pinterestDescription={pinterestDescription}
            pinterestImg={pinterestImg}
            floating
          />
        )}
      </div>

      <OptinModal entry={fromEntry} />
    </div>
  );
}
